import React, { useEffect, useState } from "react";
import { LineChart as LCIcon } from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, fmtINR } from "../lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
} from "recharts";

const SCENARIO_PRESETS = {
  Conservative: 6,
  Moderate: 10,
  Aggressive: 14,
};

export default function Projections() {
  const [years, setYears] = useState(20);
  const [inflation, setInflation] = useState(6);
  const [rates, setRates] = useState({
    Conservative: 6,
    Moderate: 10,
    Aggressive: 14,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("nominal");
  const [engine, setEngine] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          "/planning/net-worth-projection?years=20",
        );
        setEngine(data);
      } catch (_) {
        /* ignore - section hides itself when empty */
      }
    })();
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const scenario_rates = {};
      Object.entries(rates).forEach(
        ([k, v]) => (scenario_rates[k.toLowerCase()] = Number(v) / 100),
      );
      const { data } = await api.post("/projections", {
        years: Number(years),
        inflation_rate: Number(inflation),
        scenario_rates,
      });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
    // run once on mount; inputs trigger run() via the Run button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const combined = (() => {
    if (!data) return [];
    const cons = data.scenarios.conservative || [];
    const mod = data.scenarios.moderate || [];
    const agg = data.scenarios.aggressive || [];
    return cons.map((row, i) => ({
      year: row.year,
      Conservative: view === "nominal" ? cons[i]?.nominal : cons[i]?.real,
      Moderate: view === "nominal" ? mod[i]?.nominal : mod[i]?.real,
      Aggressive: view === "nominal" ? agg[i]?.nominal : agg[i]?.real,
    }));
  })();

  return (
    <DashboardLayout>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-slate-500">
          Wealth lab
        </p>
        <h1
          data-testid="projections-title"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
        >
          Projections & Scenarios
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Project your existing wealth across three scenarios over the years.
        </p>
      </div>

      {/* Engine-driven realistic net-worth path (assets + liabilities interact) */}
      <div className="card-soft p-6 mb-6" data-testid="engine-projection">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Projection Engine
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
              Realistic Net Worth Path
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              One coherent forecast — assets grow, contributions add, loans
              amortize, and goals consume their reserved assets at maturity.
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-widest bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full font-semibold">
            Engine v{engine?.assumption_version ?? 0}
          </span>
        </div>
        {!engine || (engine.series || []).length === 0 ? (
          <div className="h-72 grid place-items-center text-sm text-slate-500">
            <div className="text-center">
              <LCIcon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              Add assets &amp; liabilities to see your realistic net-worth path.
            </div>
          </div>
        ) : (
          <div className="h-80 mt-4">
            <ResponsiveContainer>
              <AreaChart data={engine.series}>
                <defs>
                  <linearGradient id="engAsset" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="engLiab" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="rgba(148,163,184,0.2)"
                />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 11 }}
                  stroke="rgba(100,116,139,0.5)"
                />
                <YAxis
                  tickFormatter={(v) => fmtINR(v, { compact: true })}
                  tick={{ fontSize: 11 }}
                  stroke="rgba(100,116,139,0.5)"
                />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="assets"
                  stroke="#10b981"
                  fill="url(#engAsset)"
                  strokeWidth={2.5}
                  name="Assets"
                />
                <Area
                  type="monotone"
                  dataKey="liabilities"
                  stroke="#ef4444"
                  fill="url(#engLiab)"
                  strokeWidth={2.5}
                  name="Liabilities"
                />
                <Line
                  type="monotone"
                  dataKey="net_worth"
                  stroke="#1e3a8a"
                  strokeWidth={3}
                  dot={false}
                  name="Net Worth"
                />
                <Line
                  type="monotone"
                  dataKey="available_net_worth"
                  stroke="#a855f7"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={false}
                  name="Available Net Worth"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 card-soft p-6 space-y-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Parameters
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Years to project
            </label>
            <input
              data-testid="proj-years"
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              min={1}
              max={50}
              className="input-soft"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Inflation %
            </label>
            <input
              data-testid="proj-inflation"
              type="number"
              step="0.5"
              value={inflation}
              onChange={(e) => setInflation(e.target.value)}
              className="input-soft"
            />
          </div>

          {Object.keys(rates).map((k) => (
            <div key={k} className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {k} return %
              </label>
              <input
                data-testid={`proj-rate-${k.toLowerCase()}`}
                type="number"
                step="0.5"
                value={rates[k]}
                onChange={(e) => setRates({ ...rates, [k]: e.target.value })}
                className="input-soft"
              />
            </div>
          ))}

          <button
            data-testid="proj-run"
            onClick={run}
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? "Computing…" : "Run projection"}
          </button>
          <button
            onClick={() => setRates({ ...SCENARIO_PRESETS })}
            className="btn-secondary w-full !text-xs"
          >
            Reset to presets
          </button>
        </div>

        <div className="lg:col-span-3 space-y-6">
          <div className="card-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  Scenario Comparison
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
                  Your wealth in {years} years
                </h3>
              </div>
              <div className="flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
                {["nominal", "real"].map((m) => (
                  <button
                    key={m}
                    data-testid={`proj-view-${m}`}
                    onClick={() => setView(m)}
                    className={`px-3 py-1 text-xs font-semibold capitalize rounded-md ${view === m ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm" : "text-slate-500"}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            {!data || combined.length === 0 ? (
              <div className="h-72 grid place-items-center text-sm text-slate-500">
                <div className="text-center">
                  <LCIcon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  Add some assets to see projections
                </div>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer>
                  <LineChart data={combined}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="rgba(148,163,184,0.2)"
                    />
                    <XAxis
                      dataKey="year"
                      tickFormatter={(v) => `Yr ${v}`}
                      tick={{ fontSize: 11 }}
                      stroke="rgba(100,116,139,0.5)"
                    />
                    <YAxis
                      tickFormatter={(v) => fmtINR(v, { compact: true })}
                      tick={{ fontSize: 11 }}
                      stroke="rgba(100,116,139,0.5)"
                    />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Conservative"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="Moderate"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="Aggressive"
                      stroke="#1e3a8a"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {data && (
            <div className="grid sm:grid-cols-3 gap-4">
              {Object.entries(data.scenarios).map(([k, series]) => {
                const last = series[series.length - 1] || {};
                const color =
                  k === "conservative"
                    ? "text-amber-600"
                    : k === "moderate"
                      ? "text-emerald-600"
                      : "text-blue-700 dark:text-blue-400";
                return (
                  <div
                    key={k}
                    data-testid={`scenario-card-${k}`}
                    className="card-soft p-5"
                  >
                    <div className="text-xs font-bold tracking-widest text-slate-500 capitalize">
                      {k}
                    </div>
                    <div
                      className={`mt-2 num-mono text-2xl font-bold ${color}`}
                    >
                      {fmtINR(view === "nominal" ? last.nominal : last.real)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      In year {data.years} ({view})
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
